import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';

/**
 * Single-colour selector popover for PE / SE — matches the SO colour input style.
 * Shows only catalogue colours (from image_colors + SKUs). Supports adding a custom colour.
 */
const ColorSelectPopover = ({ idx, row, onChange, disabled, testIdPrefix = 'pe' }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const colors = row.colors || [];

  const selectColor = (c) => {
    onChange(idx, 'color', c);
    setOpen(false);
    setAdding(false);
  };

  const addColor = (val) => {
    const trimmed = (val || '').trim();
    if (!trimmed) { setAdding(false); return; }
    selectColor(trimmed);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-color-${idx}`}
          className={`h-8 text-sm w-full rounded-md border px-2 text-left flex items-center justify-between ${disabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-background hover:border-sky-400'} ${row.color ? 'border-sky-300 text-sky-800 font-medium' : 'border-input text-gray-500'}`}
        >
          <span className="truncate">{row.color || 'Select'}</span>
          <svg className="w-3.5 h-3.5 ml-1 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0 z-50" align="start" data-testid={`${testIdPrefix}-color-popover-${idx}`}>
        <div className="max-h-60 overflow-y-auto p-1">
          {colors.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No catalogue colours — add one below.</div>}
          {colors.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => selectColor(c)}
              className={`w-full text-left px-3 py-1.5 rounded text-sm hover:bg-sky-50 ${row.color === c ? 'bg-sky-100 text-sky-800 font-semibold' : 'text-gray-700'}`}
              data-testid={`${testIdPrefix}-color-opt-${c}-${idx}`}
            >
              {c}
            </button>
          ))}

          {adding ? (
            <div className="p-1">
              <Input autoFocus placeholder="Colour name + Enter" className="h-8 text-sm"
                data-testid={`${testIdPrefix}-addcolor-input-${idx}`}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addColor(e.target.value); } else if (e.key === 'Escape') { setAdding(false); } }}
                onBlur={e => addColor(e.target.value)} />
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)}
              className="w-full text-left px-3 py-1.5 rounded text-sm text-sky-600 hover:bg-sky-50"
              data-testid={`${testIdPrefix}-addcolor-${idx}`}>+ Add colour…</button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColorSelectPopover;
